import type { ReactNode } from 'react';

import { IconBack, IconTv } from '../icons';
import { PRESS_ICON_CLASS, TAP_TARGET_CLASS } from '../styles/tokens';
import { cx } from './cx';
import styles from './Screen.module.css';

export interface ScreenProps {
  children: ReactNode;
  /** Rendered after the content, pinned to the bottom of the frame (help line). */
  footer?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/** The page frame shared by every flow screen: gutter, vertical rhythm, bottom-pinned footer. */
export function Screen({ children, footer, className, ...rest }: ScreenProps) {
  return (
    <div className={cx(styles.screen, className)} data-testid={rest['data-testid']}>
      <div className={styles.content}>
        {children}
        {footer ? (
          <>
            <div className={styles.spacer} />
            <p className={styles.footer}>{footer}</p>
          </>
        ) : null}
      </div>
    </div>
  );
}

export function ScreenTitle({ children }: { children: ReactNode }) {
  return <h1 className={styles.title}>{children}</h1>;
}

export function ScreenLead({ children }: { children: ReactNode }) {
  return <p className={styles.lead}>{children}</p>;
}

/** Brand row of M-A1/M-A2/M-A4: pink dot + wordmark, with an optional right-hand slot. */
export function BrandRow({ right }: { right?: ReactNode }) {
  return (
    <div className={styles.brandRow}>
      <div className={styles.brand}>
        <span className={styles.dot} />
        <span className={styles.brandName}>Acapella</span>
      </div>
      {right}
    </div>
  );
}

/**
 * The cyan "TV K7PX4M" pill (48:85). `/guest` reuses it for the session it is joining, so the
 * caller can replace the whole label ("Sesión MQ7Z2K") while keeping the glyph and the shape.
 */
export function TvChip({ code, label }: { code: string; label?: string }) {
  return (
    <span className={styles.tvChip}>
      <IconTv />
      {label ?? `TV ${code}`}
    </span>
  );
}

/** Back arrow + title header (M-B4 49:414). */
export function ScreenHeader({
  title,
  onBack,
  backLabel,
}: {
  title: string;
  onBack?: () => void;
  backLabel: string;
}) {
  return (
    <div className={styles.header}>
      {onBack ? (
        <button
          type="button"
          // 32 px drawn, 44 px to the finger (UX-121); the pill comes up behind it on press.
          className={cx(styles.back, TAP_TARGET_CLASS, PRESS_ICON_CLASS)}
          onClick={onBack}
          aria-label={backLabel}
        >
          <IconBack />
        </button>
      ) : null}
      <h1 className={styles.headerTitle}>{title}</h1>
    </div>
  );
}
